import { useState, useEffect } from 'react';
import { Loader2, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateClientEvent } from './useTimeline';
import { CATEGORY_LABELS, MANUAL_EVENT_TYPES } from './types';
import type { ClientEventType } from './types';

function formatLocalDateTimeInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddEventModal({ open, onOpenChange }: Props) {
  const create = useCreateClientEvent();
  const [eventType, setEventType] = useState<ClientEventType>('contact_phone');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState(formatLocalDateTimeInput(new Date()));

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (open) {
      setEventType('contact_phone');
      setTitle('');
      setDescription('');
      setEventDate(formatLocalDateTimeInput(new Date()));
    }
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSubmit = async () => {
    if (!title.trim()) return;
    try {
      await create.mutateAsync({
        eventType,
        title,
        description,
        eventDate: new Date(eventDate).toISOString(),
      });
      onOpenChange(false);
    } catch {
      // toast já mostrado
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">Novo evento na timeline</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <Select value={eventType} onValueChange={(v) => setEventType(v as ClientEventType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MANUAL_EVENT_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {CATEGORY_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="event-title">Título *</Label>
            <Input
              id="event-title"
              placeholder="Ex: Ligação de alinhamento mensal"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={create.isPending}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="event-date">Data e hora</Label>
            <Input
              id="event-date"
              type="datetime-local"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              disabled={create.isPending}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="event-desc">Descrição</Label>
            <Textarea
              id="event-desc"
              rows={4}
              placeholder="Resumo da conversa, decisões tomadas, próximos passos…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={create.isPending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={create.isPending || !title.trim()}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {create.isPending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Salvando…
              </>
            ) : (
              <>
                <Plus size={14} className="mr-2" />
                Adicionar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
